/**
 * useIraDetail — React wrapper around `IraCoreService.fetchIraDetail`.
 *
 * - Abort-capable: fresh AbortController per `planNumber` change.
 * - Exposes `reload()` so the operational flows (contribution / distribution /
 *   RMD / rollover) can re-hydrate balances + txn history after posting.
 * - Mirrors the surface of `useCdDetail` in `modules/RCD/hooks/useCdDetail.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { IraCoreService, type IraBeneficiaryRow, type IraDetail } from '../services/IraCoreService';

export interface UseIraDetailResult {
  data: IraDetail | null;
  beneficiaries: IraBeneficiaryRow[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useIraDetail(planNumber: string | undefined): UseIraDetailResult {
  const client = useApiClient();
  const abortRef = useRef<AbortController | null>(null);
  const [tick, setTick] = useState<number>(0);

  const [data, setData] = useState<IraDetail | null>(null);
  const [beneficiaries, setBeneficiaries] = useState<IraBeneficiaryRow[]>([]);
  const [loading, setLoading] = useState<boolean>(!!planNumber);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!planNumber) {
      setData(null);
      setBeneficiaries([]);
      setLoading(false);
      setError(null);
      return;
    }

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    const svc = new IraCoreService(client);
    void (async (): Promise<void> => {
      try {
        const { detail, beneficiaries: benefs } = await svc.fetchIraDetail(planNumber, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        setData(detail);
        setBeneficiaries(benefs);
        setLoading(false);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load IRA detail.');
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planNumber, client, tick]);

  const reload = useCallback((): void => setTick((t) => t + 1), []);

  return { data, beneficiaries, loading, error, reload };
}
